import React, { useRef } from "react";
import { Bold, Italic } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { renderRichText, wrapSelection } from "./richTextMarkup";

/**
 * Master v5 Prompt 30C Phase 2 — `rich` input type editor. See
 * richTextMarkup.js for why this is a constrained-markdown textarea
 * rather than a full rich-text-editor library.
 */
export function RichInputEditor({ value, onChange, disabled, testId }) {
  const ref = useRef(null);

  const apply = (marker, placeholder) => {
    if (disabled) return;
    const { next, cursor } = wrapSelection(ref.current, value || "", marker, placeholder);
    onChange(next);
    requestAnimationFrame(() => {
      ref.current?.focus();
      ref.current?.setSelectionRange(cursor, cursor);
    });
  };

  return (
    <div className="space-y-2" data-testid={testId || "rich-input-editor"}>
      <div className="flex items-center gap-1">
        <Button type="button" variant="outline" size="sm" className="h-7 w-7 p-0" disabled={disabled} onClick={() => apply("**", "bold text")} data-testid="rich-input-bold-btn">
          <Bold className="h-3.5 w-3.5" />
        </Button>
        <Button type="button" variant="outline" size="sm" className="h-7 w-7 p-0" disabled={disabled} onClick={() => apply("_", "italic text")} data-testid="rich-input-italic-btn">
          <Italic className="h-3.5 w-3.5" />
        </Button>
      </div>
      <Textarea
        ref={ref}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        rows={4}
        placeholder="Type your answer... select text and use Bold/Italic above"
        data-testid="rich-input-textarea"
      />
      {value ? (
        <div className="rounded-md border border-dashed border-border/70 px-3 py-2 text-sm text-muted-foreground" data-testid="rich-input-live-preview">
          {renderRichText(value)}
        </div>
      ) : null}
    </div>
  );
}
